/**
 * EU-747 — escalada do "Tentar novamente" da tela de erro genérica do router.
 *
 * O `reset()` do TanStack é a ação certa para um 5xx transitório (EU-636 /
 * UNI-017): re-executa o carregamento sem recarregar a página. Mas para um erro
 * que o classificador de chunk não reconheceu e que volta IGUAL depois do reset
 * — o `lazyRouteComponent` guarda o erro do `import()` em closure e o relança em
 * 0 ms a cada render — o `reset()` é inócuo para sempre, e o candidato ficava
 * clicando num botão que não podia funcionar (card EU-747, iOS).
 *
 * A regra é local e barata: a MESMA chave (`pathname|message`) anotada de novo
 * dentro da janela é uma repetição → quem exibe a tela troca o retry por um
 * recarregamento de verdade. O 1º clique continua sendo `reset()`.
 *
 * Estado em módulo, não em `useRef`: a tela de erro DESMONTA no `reset()` e a
 * próxima ocorrência é uma instância nova do componente.
 */

/** Janela em que a mesma chave de volta conta como repetição. */
export const ROUTER_ERROR_REPEAT_WINDOW_MS = 60_000;

/**
 * Duas anotações da mesma chave dentro deste intervalo são a MESMA ocorrência
 * (o double render do StrictMode em dev chama o inicializador duas vezes no
 * mesmo tick) e devolvem a mesma decisão — sem contar como repetição.
 */
export const SAME_OCCURRENCE_WINDOW_MS = 100;

let last: { key: string; at: number; repeat: boolean } | null = null;

/**
 * Anota que a tela de erro genérica foi exibida para `key` no instante `now` e
 * devolve se é uma repetição (o próximo retry deve recarregar).
 */
export function noteServerError(key: string, now: number = Date.now()): { repeat: boolean } {
  if (last !== null && last.key === key) {
    const elapsed = now - last.at;
    if (elapsed >= 0 && elapsed <= SAME_OCCURRENCE_WINDOW_MS) {
      return { repeat: last.repeat };
    }
    if (elapsed < ROUTER_ERROR_REPEAT_WINDOW_MS) {
      last = { key, at: now, repeat: true };
      return { repeat: true };
    }
  }
  last = { key, at: now, repeat: false };
  return { repeat: false };
}

/** Só para o teste — cada caso começa sem histórico. */
export function resetRouterErrorRetryForTests(): void {
  last = null;
}
